import { Button, ScrollView } from "devextreme-react";
import Form, {
  ButtonItem,
  FormRef,
  Item,
  PatternRule,
  RequiredRule,
} from "devextreme-react/form";
import { Popup } from "devextreme-react/popup";
import notify from "../../lib/notify";
import { QRCodeSVG } from "qrcode.react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUser } from "../../api/auth";
import { disable2FA, enable2FA, enable2FAAccount } from "../../api/settings";
import "./TwoFactorAuthentication.scss";

export const TwoFactorAuthentication = () => {
  const { t } = useTranslation();
  const formRef = useRef<FormRef | null>(null);

  const [qrCode, setQrCode] = useState<string | null>(null);
  const [writtenCode, setWrittenCode] = useState<string | null>(null);
  const [userEnteredCode, setUserEnteredCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isDisableModalVisible, setIsDisableModalVisible] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const openRecoveryCodesPopup = () => setIsSuccessModalVisible(true);

  const handleEnable2FA = async () => {
    setIsLoading(true);
    try {
      const response = await enable2FA();
      const userResponse = await getUser();
      const email = userResponse?.data?.email;
      if (!email) throw new Error("Unable to retrieve user's email address.");

      const issuer = "Invenio HR";
      const sharedKey = response.data?.sharedKey;
      const otpAuthUrl = `otpauth://totp/${issuer}:${email}?secret=${sharedKey}&issuer=${issuer}&digits=6`;

      setQrCode(otpAuthUrl);
      setWrittenCode(sharedKey);
      setIsModalVisible(true);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable2FAAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await enable2FAAccount(userEnteredCode);
      setCodes(response.data?.recoveryCodes || []);
      openRecoveryCodesPopup();
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
      setError(t("twoFactorAuth.error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setIsLoading(true);
    try {
      await disable2FA();
      setIsDisableModalVisible(true);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setUserEnteredCode("");
  };

  const handleCloseSuccessModal = () => setIsSuccessModalVisible(false);
  const handleCloseDisableModal = () => setIsDisableModalVisible(false);

  const handleFormSubmit = () => {
    const validationResult = formRef.current?.instance().validate();
    if (validationResult?.isValid) {
      handleCloseModal();
      handleEnable2FAAccount();
      setIsSuccessModalVisible(true);
    }
  };

  return (
    <ScrollView useNative>
      {/* Główna karta */}
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50">
        <div className="max-w-4xl w-full bg-white rounded-2xl shadow-lg p-12 text-center">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-6">
            {t("2fa.H1")}
          </h1>

          <p className="text-gray-700 text-lg text-justify leading-relaxed mb-10">
            {t("2fa.notes2Fa")}
          </p>

          <div className="flex justify-center gap-6">
            <Button
              text={t("2fa.Enable 2FA")}
              type="default"
              stylingMode="contained"
              onClick={handleEnable2FA}
              disabled={isLoading}
              className="px-10 py-3 text-base font-medium"
            />
            <Button
              text={t("2fa.Disable 2FA")}
              type="danger"
              stylingMode="outlined"
              onClick={handleDisable2FA}
              disabled={isLoading}
              className="px-10 py-3 text-base font-medium"
            />
          </div>
        </div>
      </div>

      {/* --- Popup: skanowanie QR --- */}
      <Popup
        visible={isModalVisible}
        onHiding={handleCloseModal}
        maxWidth={450}
        height="auto"
        showTitle
        title={t("2fa.Scan the QR code")}
        showCloseButton
        dragEnabled
      >
        <div className="flex flex-col items-center px-6 pb-4 text-center">
          <p className="mb-4 text-gray-700 text-sm">
            {t("2fa.Scan the QR code in your app")}
          </p>
          <QRCodeSVG value={qrCode ?? ""} size={200} />
          <p className="mt-5 text-gray-700 text-sm font-medium">
            {t("2fa.Written code")}:
          </p>
          <p className="font-mono font-bold text-lg text-blue-700 mb-4">
            {writtenCode}
          </p>

          <p className="text-gray-700 text-sm mb-3">
            {t("2fa.enterSixDigitCodeDisplayedInApp")}
          </p>

          <Form ref={formRef} colCount={4} className="w-full mt-2">
            <Item
              dataField={t("2fa.yourCode")}
              editorType="dxTextBox"
              editorOptions={{
                value: userEnteredCode,
                onValueChanged: (e: any) => setUserEnteredCode(e.value),
                maxLength: 6,
                disabled: isLoading,
                placeholder: t("2fa.Code"),
              }}
              label={{ visible: false }}
              colSpan={3}
            >
              <RequiredRule message={t("2fa.code is required")} />
              <PatternRule
                pattern="^\\d{6}$"
                message={t("2fa.codeShouldConsistOfSixDigits")}
              />
            </Item>

            <ButtonItem
              horizontalAlignment="left"
              verticalAlignment="bottom"
              buttonOptions={{
                text: t("common.send"),
                type: "default",
                onClick: handleFormSubmit,
                disabled: isLoading,
                width: "100%",
              }}
            />
          </Form>
        </div>
      </Popup>

      {/* --- Popup: kody odzyskiwania --- */}
      <Popup
        visible={isSuccessModalVisible}
        onHiding={handleCloseSuccessModal}
        maxWidth={520}
        height="auto"
        showTitle
        title={t("2fa.recoveryCodes.header")}
        showCloseButton
        dragEnabled
      >
        <div className="px-6 py-4 text-gray-700">
          <p className="mb-4 text-base leading-relaxed text-justify">
            {t("2fa.Here are your recovery codes")}
          </p>
          {error && <p className="text-red-600 mb-2">{error}</p>}
          {codes.length > 0 ? (
            <ul className="list-disc list-inside space-y-2 text-gray-800 text-base font-mono bg-gray-50 rounded-md p-4">
              {codes.map((code, index) => (
                <li key={index}>{code}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">{t("2fa.No recovery codes available.")}</p>
          )}
        </div>
      </Popup>

      {/* --- Popup: wyłączenie 2FA --- */}
      <Popup
        visible={isDisableModalVisible}
        onHiding={handleCloseDisableModal}
        maxWidth={420}
        height="auto"
        showTitle
        title={t("common.information")}
        showCloseButton
        dragEnabled
      >
        <div className="text-center py-6 text-gray-700 text-base leading-relaxed">
          {t("2fa.Two-factor authentication has been disabled.")}
        </div>
      </Popup>
    </ScrollView>
  );
};
